import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Modal, Table } from 'react-bootstrap';
import dayjs from 'dayjs';
import ReactECharts from 'echarts-for-react';
import FullPageSpinner from 'components/common/full-page-spinner';
import { getRejectLogs } from 'services/productionOverviewService';
import QualityFactoryMap from './QualityFactoryMap';

const AUTO_REFRESH_INTERVAL = 30 * 1000;

const TREEMAP_COLORS = ['#c0392b', '#e74c3c', '#d35400', '#e67e22', '#f39c12', '#f1c40f', '#27ae60', '#2ecc71', '#2980b9', '#3498db', '#8e44ad', '#9b59b6'];

function getDuration(record) {
  if (!record.created || !record.timeErrorWasMade) return '-';
  const diff = dayjs(record.created).diff(dayjs(record.timeErrorWasMade), 'minute');
  if (diff <= 0) return '-';
  if (diff < 60) return `${diff} min`;
  const h = Math.floor(diff / 60);
  const m = diff % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function buildStageGroups(records) {
  const grouped = {};
  records.forEach(r => {
    const stage = r.stageName || 'Unknown';
    if (!grouped[stage]) grouped[stage] = [];
    grouped[stage].push(r);
  });

  return Object.entries(grouped)
    .map(([name, items]) => ({ name, count: items.length, records: items }))
    .sort((a, b) => b.count - a.count);
}

function buildTreemapData(stageGroups) {
  return stageGroups.map((g, i) => ({
    name: g.name,
    value: g.count,
    itemStyle: { color: TREEMAP_COLORS[i % TREEMAP_COLORS.length] }
  }));
}

function getTreemapOptions(data) {
  return {
    tooltip: {
      formatter: params => {
        return `<strong>${params.name}</strong><br/>Rejected: ${params.value}`;
      }
    },
    series: [
      {
        type: 'treemap',
        data,
        roam: false,
        nodeClick: false,
        breadcrumb: { show: false },
        label: {
          show: true,
          formatter: '{b}\n{c}',
          fontSize: 14,
          fontWeight: 'bold',
          color: '#fff'
        },
        itemStyle: {
          borderColor: '#fff',
          borderWidth: 2,
          gapWidth: 2
        },
        emphasis: {
          itemStyle: {
            borderColor: '#333',
            borderWidth: 3
          }
        }
      }
    ]
  };
}

function DetailModal({ title, records, onHide }) {
  const show = Boolean(title);

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          Rejected Logs — {title} ({records.length})
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {records.length > 0 ? (
          <Table striped bordered hover size="sm" responsive>
            <thead>
              <tr>
                <th>#</th>
                <th>Job No</th>
                <th>Win No</th>
                <th>Product Type</th>
                <th>Product</th>
                <th>Fault Group</th>
                <th>Fault Direct</th>
                <th>Duration</th>
                <th>Rejected By</th>
                <th>Registered Time</th>
                <th>Stage</th>
              </tr>
            </thead>
            <tbody>
              {records.map((r, i) => (
                <tr key={r.id ?? i}>
                  <td>{i + 1}</td>
                  <td>{r.jobNo}</td>
                  <td>{r.winNo}</td>
                  <td>{r.productType}</td>
                  <td>{r.product}</td>
                  <td>{r.faultGroup}</td>
                  <td>{r.faultDesc}</td>
                  <td>{getDuration(r)}</td>
                  <td>{r.userName}</td>
                  <td>{r.created ? dayjs(r.created).format('DD/MM/YYYY HH:mm') : '-'}</td>
                  <td>{r.stageName}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <div className="text-center text-muted py-4">No records for this stage.</div>
        )}
      </Modal.Body>
    </Modal>
  );
}

DetailModal.propTypes = {
  title: PropTypes.string,
  records: PropTypes.array.isRequired,
  onHide: PropTypes.func.isRequired
};

export default function RejectLogPanel({ date }) {
  const [allRecords, setAllRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productTypeFilter, setProductTypeFilter] = useState('');
  const [productFilter, setProductFilter] = useState('');
  const [selectedStage, setSelectedStage] = useState(null);
  const [view, setView] = useState('map');
  const timerRef = useRef(null);

  const loadData = useCallback(
    (showLoading = false) => {
      if (showLoading) setLoading(true);
      return getRejectLogs(date)
        .then(result => {
          const records = result?.data?.data || result?.data || [];
          setAllRecords(Array.isArray(records) ? records : []);
        })
        .catch(err => console.error('Error fetching reject logs', err))
        .finally(() => {
          if (showLoading) setLoading(false);
        });
    },
    [date]
  );

  useEffect(() => {
    setLoading(true);
    loadData(true);
  }, [loadData]);

  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);

    const isToday = dayjs(date, 'YYYY-MM-DD').isSame(new Date(), 'day');
    if (isToday) {
      timerRef.current = setInterval(() => loadData(false), AUTO_REFRESH_INTERVAL);
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [date, loadData]);

  const productTypes = useMemo(() => {
    const set = new Set(allRecords.map(r => r.productType).filter(Boolean));
    return [...set].sort();
  }, [allRecords]);

  const products = useMemo(() => {
    let source = allRecords;
    if (productTypeFilter) {
      source = source.filter(r => r.productType === productTypeFilter);
    }
    const set = new Set(source.map(r => r.product).filter(Boolean));
    return [...set].sort();
  }, [allRecords, productTypeFilter]);

  const filtered = useMemo(() => {
    let result = allRecords;
    if (productTypeFilter) result = result.filter(r => r.productType === productTypeFilter);
    if (productFilter) result = result.filter(r => r.product === productFilter);
    return result;
  }, [allRecords, productTypeFilter, productFilter]);

  const stageGroups = useMemo(() => buildStageGroups(filtered), [filtered]);
  const treemapData = useMemo(() => buildTreemapData(stageGroups), [stageGroups]);

  const handleChartClick = useCallback(params => {
    if (params?.name) {
      setSelectedStage({ label: params.name, names: [params.name] });
    }
  }, []);

  const modalRecords = useMemo(() => {
    if (!selectedStage) return [];
    return filtered.filter(r => selectedStage.names.includes(r.stageName));
  }, [filtered, selectedStage]);

  const onEvents = useMemo(
    () => ({
      click: handleChartClick
    }),
    [handleChartClick]
  );

  const handleProductTypeChange = e => {
    setProductTypeFilter(e.target.value);
    setProductFilter('');
  };

  return (
    <div style={{ position: 'relative' }}>
      <FullPageSpinner show={loading} quantity={3} />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h3 style={{ margin: 0 }}>Quality — Rejected Logs</h3>
        <span style={{ fontSize: 13, color: '#6c757d' }}>
          Total rejected: <strong>{filtered.length}</strong>
        </span>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button
          type="button"
          className={`quality-view-toggle-btn ${view === 'map' ? 'is-active' : ''}`}
          style={{ padding: '6px 14px', fontSize: 13, fontWeight: 500, borderRadius: 6, cursor: 'pointer' }}
          onClick={() => setView('map')}
        >
          Factory Map
        </button>
        <button
          type="button"
          className={`quality-view-toggle-btn ${view === 'heatmap' ? 'is-active' : ''}`}
          style={{ padding: '6px 14px', fontSize: 13, fontWeight: 500, borderRadius: 6, cursor: 'pointer' }}
          onClick={() => setView('heatmap')}
        >
          Heat Map (by Stage)
        </button>
      </div>

      <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
        <Form.Group style={{ minWidth: 200 }}>
          <Form.Label style={{ marginBottom: 4, fontSize: 13, fontWeight: 500 }}>Product Type</Form.Label>
          <Form.Select size="sm" value={productTypeFilter} onChange={handleProductTypeChange}>
            <option value="">All Product Types</option>
            {productTypes.map(pt => (
              <option key={pt} value={pt}>
                {pt}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group style={{ minWidth: 200 }}>
          <Form.Label style={{ marginBottom: 4, fontSize: 13, fontWeight: 500 }}>Product</Form.Label>
          <Form.Select size="sm" value={productFilter} onChange={e => setProductFilter(e.target.value)}>
            <option value="">All Products</option>
            {products.map(p => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </div>

      {filtered.length > 0 ? (
        view === 'map' ? (
          <QualityFactoryMap stageGroups={stageGroups} onStageClick={setSelectedStage} />
        ) : (
          <div style={{ border: '1px solid #dee2e6', borderRadius: 6, backgroundColor: '#fff', padding: 16 }}>
            <ReactECharts option={getTreemapOptions(treemapData)} style={{ height: 500 }} onEvents={onEvents} />
            <div style={{ fontSize: 12, color: '#6c757d', textAlign: 'center', marginTop: 8 }}>Click on a section to view rejected log details</div>
          </div>
        )
      ) : (
        <div style={{ padding: 24, textAlign: 'center', border: '1px solid #dee2e6', borderRadius: 6 }}>No rejected log data available for this date.</div>
      )}

      <DetailModal title={selectedStage?.label} records={modalRecords} onHide={() => setSelectedStage(null)} />
    </div>
  );
}

RejectLogPanel.propTypes = {
  date: PropTypes.string.isRequired
};
